import { Mode, applyMode } from '@cloudscape-design/global-styles';

export interface NavigationPanelState {
  collapsed?: boolean;
  collapsedSections?: Record<number, boolean>;
}

const PREFIX = 'pronetx-console';
const THEME_STORAGE_NAME = `${PREFIX}-theme`;
const NAVIGATION_PANEL_STATE_STORAGE_NAME = `${PREFIX}-navigation-panel-state`;
const COMPANY_EMAIL_STORAGE_NAME = `${PREFIX}-company-email`;
const COMPANY_ID_STORAGE_NAME = `${PREFIX}-company-id`;
const SELECTED_INSTANCE_ID_STORAGE_NAME = `${PREFIX}-selected-instance-id`;

export abstract class StorageHelper {
  static getTheme(): Mode {
    return (localStorage.getItem(THEME_STORAGE_NAME) as Mode) ?? Mode.Light;
  }

  static applyTheme(theme: Mode) {
    localStorage.setItem(THEME_STORAGE_NAME, theme);
    applyMode(theme);

    document.documentElement.style.setProperty('--app-color-scheme', theme);

    return theme;
  }

  static getNavigationPanelState(): NavigationPanelState {
    const value =
      localStorage.getItem(NAVIGATION_PANEL_STATE_STORAGE_NAME) ??
      JSON.stringify({
        collapsed: true
      });

    let state: NavigationPanelState | null = null;
    try {
      state = JSON.parse(value);
    } catch {
      state = {};
    }

    return state ?? {};
  }

  static setNavigationPanelState(state: Partial<NavigationPanelState>) {
    const currentState = this.getNavigationPanelState();
    const newState = { ...currentState, ...state };
    const stateStr = JSON.stringify(newState);
    localStorage.setItem(NAVIGATION_PANEL_STATE_STORAGE_NAME, stateStr);

    return newState;
  }

  static getCompanyId(): string | null {
    return localStorage.getItem(COMPANY_ID_STORAGE_NAME);
  }

  static setCompanyId(companyId: string) {
    localStorage.setItem(COMPANY_ID_STORAGE_NAME, companyId);
  }
  static clearCompanyId() {
    localStorage.removeItem(COMPANY_ID_STORAGE_NAME);
  }

  static getCompanyEmail(): string | null {
    return localStorage.getItem(COMPANY_EMAIL_STORAGE_NAME);
  }

  static setCompanyEmail(companyEmail: string) {
    localStorage.setItem(COMPANY_EMAIL_STORAGE_NAME, companyEmail);
  }

  static clearCompanyEmail() {
    localStorage.removeItem(COMPANY_EMAIL_STORAGE_NAME);
    localStorage.removeItem(COMPANY_ID_STORAGE_NAME);
  }

  static getStoredInstanceId(): string | null {
    return localStorage.getItem(SELECTED_INSTANCE_ID_STORAGE_NAME);
  }

  static setStoredInstanceId(instanceId: string) {
    localStorage.setItem(SELECTED_INSTANCE_ID_STORAGE_NAME, instanceId);
  }

  static clearStoredInstanceId() {
    localStorage.removeItem(SELECTED_INSTANCE_ID_STORAGE_NAME);
  }

  static clearCasesState(prefix = 'cases') {
    if (typeof localStorage === 'undefined') return;

    const filterPrefix = `${prefix}_filters_`;
    const paginationPrefix = `${prefix}_pagination_`;

    // Get all localStorage keys and filter for cases-related keys
    const keysToRemove = Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i))
      .filter((key): key is string => key !== null)
      .filter((key) => key.startsWith(filterPrefix) || key.startsWith(paginationPrefix));

    // Clear table preferences for both all-cases and customer-cases tables
    const tableKeys = [
      'all_cases_table_visible',
      'all_cases_table_order',
      'customer_cases_table_visible',
      'customer_cases_table_order'
    ];

    tableKeys.forEach((key) => localStorage.removeItem(key));

    // Remove filtered keys
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }
}
